/**
 * Mobile touch UI controls — horizontally scrollable building bar at bottom,
 * floating action buttons (pill/artifact/tech) on the right side.
 *
 * Uses DeviceProfile for responsive sizing across phone size tiers.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { BuildingKind } from './client_message_protocol';
import { GameClientWorldState } from './game_client_world_state';
import { getDeviceProfile, resetDeviceProfile, DeviceProfile } from './device_profile';

const MOBILE_BUILD_KINDS: BuildingKind[] = [
  BuildingKind.SpiritRoot,
  BuildingKind.GatheringArray,
  BuildingKind.HerbFarm,
  BuildingKind.TransporterStation,
  BuildingKind.RefiningArray,
  BuildingKind.AlchemyFurnace,
  BuildingKind.Forge,
  BuildingKind.SwordTower,
  BuildingKind.SlowTrap,
  BuildingKind.DefenseTower,
  BuildingKind.PuppetWorkshop,
  BuildingKind.PuppetBarracks,
];

const BUILD_LABELS: Record<BuildingKind, string> = {
  [BuildingKind.SpiritRoot]: '灵',
  [BuildingKind.GatheringArray]: '采',
  [BuildingKind.HerbFarm]: '药',
  [BuildingKind.TransporterStation]: '运',
  [BuildingKind.RefiningArray]: '炼',
  [BuildingKind.AlchemyFurnace]: '丹',
  [BuildingKind.Forge]: '锻',
  [BuildingKind.SwordTower]: '剑',
  [BuildingKind.SlowTrap]: '缓',
  [BuildingKind.PuppetBarracks]: '兵',
  [BuildingKind.DefenseTower]: '防',
  [BuildingKind.PuppetWorkshop]: '傀',
};

const BUILD_COLORS: Record<BuildingKind, number> = {
  [BuildingKind.SpiritRoot]: 0x44ff44,
  [BuildingKind.GatheringArray]: 0x4488ff,
  [BuildingKind.HerbFarm]: 0x44ff88,
  [BuildingKind.TransporterStation]: 0xff8800,
  [BuildingKind.RefiningArray]: 0xff44ff,
  [BuildingKind.AlchemyFurnace]: 0xff44aa,
  [BuildingKind.Forge]: 0xcccccc,
  [BuildingKind.SwordTower]: 0xff4444,
  [BuildingKind.SlowTrap]: 0x4488cc,
  [BuildingKind.PuppetBarracks]: 0xcc4444,
  [BuildingKind.DefenseTower]: 0xffffff,
  [BuildingKind.PuppetWorkshop]: 0xaa44ff,
};

interface BuildingButton {
  container: Container;
  bg: Graphics;
  label: Text;
  kind: BuildingKind;
}

interface ActionButton {
  container: Container;
  bg: Graphics;
  label: Text;
}

export class UiMobileControls {
  public onBuildingSelected: ((kind: BuildingKind) => void) | null = null;
  public onConsumePill: (() => void) | null = null;
  public onConsumeArtifact: (() => void) | null = null;
  public onToggleTech: (() => void) | null = null;

  private container: Container;
  private screenWidth: number;
  private screenHeight: number;
  private profile: DeviceProfile;
  private selectedIndex: number = -1;

  private barBg: Graphics;
  private barMask: Graphics;
  private barContainer: Container;
  private buildingButtons: BuildingButton[] = [];
  private selectionHighlight: Graphics;
  private pillButton: ActionButton;
  private artifactButton: ActionButton;
  private techButton: ActionButton;

  // Scroll
  private scrollX = 0;
  private maxScrollX = 0;
  private scrollStartX = 0;
  private scrollStartScrollX = 0;
  private isScrolling = false;

  constructor(parent: Container, screenWidth: number, screenHeight: number) {
    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.profile = getDeviceProfile();
    this.container = new Container();
    parent.addChild(this.container);

    this.barBg = new Graphics();
    this.barMask = new Graphics();
    this.container.addChild(this.barBg);
    this.container.addChild(this.barMask);

    this.barContainer = new Container();
    this.barContainer.mask = this.barMask;
    this.container.addChild(this.barContainer);

    this.drawBarBg();
    this.drawBarMask();
    this.createBuildingButtons();

    this.selectionHighlight = new Graphics();
    this.container.addChild(this.selectionHighlight);

    this.pillButton = this.createActionButton('丹', 0xffaa44, 0);
    this.pillButton.container.on('pointertap', () => this.onConsumePill?.());

    this.artifactButton = this.createActionButton('器', 0xff8800, 1);
    this.artifactButton.container.on('pointertap', () => this.onConsumeArtifact?.());

    this.techButton = this.createActionButton('技', 0x6666ff, 2);
    this.techButton.container.on('pointertap', () => this.onToggleTech?.());
  }

  private get p(): DeviceProfile { return this.profile; }

  private drawBarBg(): void {
    const bh = this.p.bottomBarHeight;
    const safeBottom = this.p.safeBottom;
    // Extend bg into safe area so it doesn't look cut off
    this.barBg.clear();
    this.barBg.rect(0, this.screenHeight - bh - safeBottom, this.screenWidth, bh + safeBottom);
    this.barBg.fill({ color: 0x000000, alpha: 0.6 });
    // Top border
    this.barBg.rect(0, this.screenHeight - bh - safeBottom, this.screenWidth, 1);
    this.barBg.fill({ color: 0x444444, alpha: 0.5 });
  }

  private drawBarMask(): void {
    const bh = this.p.bottomBarHeight;
    const rightPad = this.p.actionButtonSize + 14;
    const y = this.screenHeight - bh - this.p.safeBottom;
    this.barMask.clear();
    this.barMask.rect(4, y, this.screenWidth - 4 - rightPad, bh);
    this.barMask.fill({ color: 0xffffff, alpha: 1 });
  }

  private createBuildingButtons(): void {
    const bh = this.p.bottomBarHeight;
    const bs = this.p.buildingButtonSize;
    const bg = this.p.buildingButtonGap;
    const y = this.screenHeight - bh - this.p.safeBottom + (bh - bs) / 2;

    MOBILE_BUILD_KINDS.forEach((kind, i) => {
      const container = new Container();
      container.x = 4 + i * (bs + bg);
      container.y = y;

      const g = new Graphics();
      const color = BUILD_COLORS[kind] ?? 0x888888;
      g.roundRect(0, 0, bs, bs, Math.max(4, bs / 10));
      g.fill({ color, alpha: 0.3 });
      container.addChild(g);

      const fs = this.p.barFontSize;
      const label = new Text({
        text: BUILD_LABELS[kind] ?? '?',
        style: new TextStyle({
          fontFamily: 'monospace',
          fontSize: fs,
          fontWeight: 'bold',
          fill: 0xffffff,
        }),
      });
      label.anchor.set(0.5);
      label.x = bs / 2;
      label.y = bs / 2;
      container.addChild(label);

      container.eventMode = 'static';
      container.cursor = 'pointer';
      container.on('pointertap', () => {
        if (!this.isScrolling) this.selectBuilding(i, kind);
      });

      container.on('touchstart', (e: any) => {
        this.scrollStartX = e.globalX;
        this.scrollStartScrollX = this.scrollX;
        this.isScrolling = false;
      });
      container.on('touchmove', (e: any) => {
        const dx = e.globalX - this.scrollStartX;
        if (Math.abs(dx) > 5) {
          this.isScrolling = true;
          this.scrollX = Math.max(this.maxScrollX, Math.min(0, this.scrollStartScrollX + dx));
          this.applyScroll();
        }
      });

      this.barContainer.addChild(container);
      this.buildingButtons.push({ container, bg: g, label, kind });
    });

    const totalW = MOBILE_BUILD_KINDS.length * (bs + bg) - bg;
    const rightPad = this.p.actionButtonSize + 14;
    const visibleW = this.screenWidth - 4 - rightPad;
    this.maxScrollX = Math.min(0, visibleW - totalW);
  }

  private applyScroll(): void {
    this.barContainer.x = this.scrollX;
    this.updateSelectionHighlight();
  }

  private selectBuilding(index: number, kind: BuildingKind): void {
    this.selectedIndex = index;
    this.updateButtonAlphas();
    this.updateSelectionHighlight();
    this.onBuildingSelected?.(kind);
  }

  private updateButtonAlphas(): void {
    const bs = this.p.buildingButtonSize;
    const r = Math.max(4, bs / 10);
    this.buildingButtons.forEach((btn, i) => {
      btn.bg.clear();
      const color = BUILD_COLORS[btn.kind] ?? 0x888888;
      btn.bg.roundRect(0, 0, bs, bs, r);
      btn.bg.fill({ color, alpha: i === this.selectedIndex ? 0.85 : 0.3 });
    });
  }

  private updateSelectionHighlight(): void {
    const bs = this.p.buildingButtonSize;
    const r = Math.max(4, bs / 10);
    this.selectionHighlight.clear();
    if (this.selectedIndex >= 0 && this.selectedIndex < this.buildingButtons.length) {
      const btn = this.buildingButtons[this.selectedIndex];
      this.selectionHighlight.roundRect(btn.container.x + this.scrollX, btn.container.y, bs, bs, r);
      this.selectionHighlight.stroke({ color: 0x00ff00, width: 3 });
    }
  }

  private createActionButton(labelText: string, color: number, index: number): ActionButton {
    const as = this.p.actionButtonSize;
    const ag = this.p.actionButtonGap;
    const container = new Container();
    // Right-aligned, vertically centered in upper portion
    const x = this.screenWidth - as - 8;
    const topArea = this.screenHeight - this.p.bottomBarHeight - this.p.safeBottom;
    const totalH = 3 * as + 2 * ag;
    const startY = Math.max(20, (topArea - totalH) / 2);
    container.x = x;
    container.y = startY + index * (as + ag);

    const bg = new Graphics();
    bg.roundRect(0, 0, as, as, Math.max(6, as / 7));
    bg.fill({ color, alpha: 0.7 });
    container.addChild(bg);

    const fs = Math.round(as * 0.38);
    const label = new Text({
      text: labelText,
      style: new TextStyle({
        fontFamily: 'monospace',
        fontSize: fs,
        fontWeight: 'bold',
        fill: 0xffffff,
      }),
    });
    label.anchor.set(0.5);
    label.x = as / 2;
    label.y = as / 2;
    container.addChild(label);

    container.eventMode = 'static';
    container.cursor = 'pointer';
    this.container.addChild(container);
    return { container, bg, label };
  }

  show(): void { this.container.visible = true; }
  hide(): void { this.container.visible = false; }
  isVisible(): boolean { return this.container.visible; }

  update(worldState: GameClientWorldState): void {
    this.pillButton.label.text = worldState.pill > 0 ? `丹×${worldState.pill}` : '丹';
    this.artifactButton.label.text = worldState.artifact > 0 ? `器×${worldState.artifact}` : '器';
  }

  handleResize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;
    resetDeviceProfile();
    this.profile = getDeviceProfile();

    // Rebuild all visuals
    this.drawBarBg();
    this.drawBarMask();

    const bh = this.p.bottomBarHeight;
    const bs = this.p.buildingButtonSize;
    const bg = this.p.buildingButtonGap;
    const y = this.screenHeight - bh - this.p.safeBottom + (bh - bs) / 2;

    this.buildingButtons.forEach((btn, i) => {
      btn.container.x = 4 + i * (bs + bg);
      btn.container.y = y;
    });

    const totalW = MOBILE_BUILD_KINDS.length * (bs + bg) - bg;
    const rightPad = this.p.actionButtonSize + 14;
    const visibleW = this.screenWidth - 4 - rightPad;
    this.maxScrollX = Math.min(0, visibleW - totalW);
    this.scrollX = 0;
    this.applyScroll();
    this.updateSelectionHighlight();

    // Reposition action buttons
    const as = this.p.actionButtonSize;
    const ag = this.p.actionButtonGap;
    const topArea = this.screenHeight - bh - this.p.safeBottom;
    const totalH = 3 * as + 2 * ag;
    const startY = Math.max(20, (topArea - totalH) / 2);
    const x = this.screenWidth - as - 8;
    this.pillButton.container.x = x;
    this.pillButton.container.y = startY + 0 * (as + ag);
    this.artifactButton.container.x = x;
    this.artifactButton.container.y = startY + 1 * (as + ag);
    this.techButton.container.x = x;
    this.techButton.container.y = startY + 2 * (as + ag);
  }
}
